import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppNotification } from '@zunelo/shared';
import type { CurrentUserIdentity } from '../../domain/types';
import { countUnseenNotifications, sortNotificationsNewestFirst } from '../../domain/notifications';
import { notificationsClient } from '../../services/clients';

export interface ForegroundNotification {
  title: string;
  body: string;
  appPath?: string;
  externalUrl?: string;
}

function seenStorageKey(uid: string): string {
  return `zunelo_notifications_seen_${uid}`;
}

function readSeenThrough(uid: string | undefined): string | null {
  if (!uid || typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(seenStorageKey(uid));
  } catch {
    return null;
  }
}

function writeSeenThrough(uid: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(seenStorageKey(uid), value);
  } catch {
    // Seen state remains valid for this session when persistent storage is unavailable.
  }
}

interface NotificationsState {
  uid?: string;
  items: AppNotification[];
  loading: boolean;
  error: string | null;
}

function foregroundFromNotification(notification: AppNotification): ForegroundNotification {
  return {
    title: notification.title,
    body: notification.body,
    ...(notification.appPath ? { appPath: notification.appPath } : {}),
    ...(notification.externalUrl ? { externalUrl: notification.externalUrl } : {}),
  };
}

export function useNotificationsSlice(currentUser?: CurrentUserIdentity) {
  const uid = currentUser?.uid;
  const activeUid = useRef(uid);
  activeUid.current = uid;
  const [state, setState] = useState<NotificationsState>({
    uid,
    items: [],
    loading: Boolean(uid),
    error: null,
  });
  const [seenState, setSeenState] = useState(() => ({ uid, value: readSeenThrough(uid) }));
  const [foregroundState, setForegroundState] = useState<{
    uid: string;
    notification: ForegroundNotification;
  } | null>(null);

  const notifications = useMemo(
    () => (state.uid === uid ? state.items : []),
    [state.items, state.uid, uid],
  );
  const notificationsLoading = state.uid === uid ? state.loading : Boolean(uid);
  const notificationsError = state.uid === uid ? state.error : null;
  const seenThrough = seenState.uid === uid ? seenState.value : readSeenThrough(uid);
  const foregroundNotification =
    foregroundState && foregroundState.uid === uid ? foregroundState.notification : null;

  const refreshNotifications = useCallback(async (): Promise<AppNotification[] | undefined> => {
    const requestedUid = uid;
    if (!requestedUid) return undefined;
    setState((current) => ({
      uid: requestedUid,
      items: current.uid === requestedUid ? current.items : [],
      loading: true,
      error: null,
    }));
    try {
      const result = await notificationsClient.listNotifications();
      if (activeUid.current !== requestedUid) return undefined;
      const items = sortNotificationsNewestFirst(result.data ?? []);
      setState({ uid: requestedUid, items, loading: false, error: null });
      return items;
    } catch (error) {
      if (activeUid.current !== requestedUid) return undefined;
      setState({
        uid: requestedUid,
        items: [],
        loading: false,
        error: error instanceof Error ? error.message : 'No se pudieron cargar las notificaciones.',
      });
      return undefined;
    }
  }, [uid]);

  useEffect(() => {
    setState({ uid, items: [], loading: Boolean(uid), error: null });
    setSeenState({ uid, value: readSeenThrough(uid) });
    setForegroundState(null);
    if (uid) void refreshNotifications();
  }, [uid, refreshNotifications]);

  useEffect(() => {
    const serviceWorker = navigator.serviceWorker;
    if (!serviceWorker) return;
    const onMessage = (event: MessageEvent) => {
      const message = event.data as { type?: string; data?: { notificationId?: string } };
      if (message.type !== 'zunelo-push') return;
      const messageUid = uid;
      const notificationId = message.data?.notificationId;
      if (!messageUid) return;
      void refreshNotifications().then((items) => {
        const matching = notificationId
          ? items?.find(({ id }) => id === notificationId)
          : undefined;
        if (!matching || activeUid.current !== messageUid) return;
        setForegroundState({
          uid: messageUid,
          notification: foregroundFromNotification(matching),
        });
      });
    };
    serviceWorker.addEventListener('message', onMessage);
    return () => serviceWorker.removeEventListener('message', onMessage);
  }, [refreshNotifications, uid]);

  const markNotificationsSeen = useCallback(() => {
    const latest = notifications[0]?.updatedAt;
    if (!uid || !latest) return;
    writeSeenThrough(uid, latest);
    setSeenState({ uid, value: latest });
  }, [notifications, uid]);

  const unseenNotificationCount = useMemo(
    () => countUnseenNotifications(notifications, seenThrough),
    [notifications, seenThrough],
  );

  const dismissForegroundNotification = useCallback(() => setForegroundState(null), []);

  return useMemo(
    () => ({
      notifications,
      notificationsLoading,
      notificationsError,
      unseenNotificationCount,
      foregroundNotification,
      refreshNotifications,
      markNotificationsSeen,
      dismissForegroundNotification,
    }),
    [
      notifications,
      notificationsLoading,
      notificationsError,
      unseenNotificationCount,
      foregroundNotification,
      refreshNotifications,
      markNotificationsSeen,
      dismissForegroundNotification,
    ],
  );
}
